import {Checkbox} from '../../ui/Mobile';
import {Disclosure} from '../../ui/Mobile';
import {PageTitle,ListRow} from '../../ui/Mobile';
import {Input, Textarea, Button} from '../../ui/Mobile';
import { LoadingPage } from '../../ui/States';
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Link, useNavigate, useSearchParams } from "../../app/navigation";
import { useLiveQuery } from "dexie-react-hooks";
import { useService } from "../../app/context";
import { usePendingNavigation } from "../../app/usePendingNavigation";
import { SopService } from "../../application/sop-service";
import {
  blankSop,
  sopStateLabels,
  sopResultLabels,
  type SopFields,
  type SopRun,
  type SopVersion,
  type SopResult,
} from "../../domain/sop/model";
function Checklist({ version }: { version: SopVersion }) {
  return (
    <ol>
      {version.steps.map((s) => (
        <li key={s.id}>
          <h3>
            {s.group && <span>{s.group} / </span>}
            {s.title} · {s.required ? "必做" : "选做"}
          </h3>
          <p className="capture-body">{s.body}</p>
        </li>
      ))}
    </ol>
  );
}
export function SopList({view="home"}: {view?:"home"|"history"|"manage"|"pinned"|"recent"|"drafts"}) {
  const { db } = useService();
  const rows = useLiveQuery(async () => {
    const templates = await db.sopTemplates.toArray(),
      versions = new Map(
        (await db.sopTemplateVersions.toArray()).map((v) => [v.id, v]),
      ),
      runs = await db.sopRuns.toArray();
    return {
      templates: templates
        .filter((t) => !t.deletedAt)
        .map((t) => ({ t, v: versions.get(t.currentVersionId)! })),
      runs: runs
        .filter((r) => !r.deletedAt)
        .map((r) => ({ r, v: versions.get(r.templateVersionId)! })),
      drafts: (await db.sopDrafts.toArray()).filter((d) => !d.deletedAt),
    };
  }, [db]);
  if (!rows)
    return (
      <main className="page" id="main">
        正在读取…
      </main>
    );
  const active=rows.runs.filter(({r})=>['active','paused'].includes(r.state));
  const history=rows.runs.filter(({r})=>!['active','paused'].includes(r.state)).sort((a,b)=>b.r.updatedAt.localeCompare(a.r.updatedAt));
  const pinned=rows.templates.filter(({t})=>t.pinned&&!t.archivedAt);
  const recent=rows.templates.filter(({t})=>rows.runs.some(({r})=>r.templateId===t.id)).sort((a,b)=>{
    const last=(id:string)=>rows.runs.filter(({r})=>r.templateId===id).map(({r})=>r.updatedAt).sort().at(-1)??'';
    return last(b.t.id).localeCompare(last(a.t.id));
  });
  const templateRow=({t,v}:typeof rows.templates[number])=><ListRow key={t.id} to={'/sop/templates/'+t.id} title={v.title} note={'版本 '+v.version+(t.archivedAt?' · 归档':'')}/>;
  const runRow=({r,v}:typeof rows.runs[number])=><ListRow key={r.id} to={'/sop/runs/'+r.id} title={v.title} note={sopStateLabels[r.state]+' · 版本 '+v.version+' · '+(r.endedAt?.slice(0,10)??'已完成 '+r.results.filter(x=>x.status==='completed').length+'/'+r.results.length)+(r.archivedAt?' · 归档':'')}/>;
  const titles={home:'SOP',history:'执行历史',manage:'SOP 管理',pinned:'固定模板',recent:'最近使用',drafts:'模板草稿'};
  return <main className={'page sop-page '+(view==='home'?'module-landing':'secondary-page')} id="main" data-ready="true">
    <PageTitle secondary={view!=='home'}>{titles[view]}</PageTitle>
    {view==='home'&&<>
      {!!active.length&&<section className="sop-current focus-surface"><h2 aria-label={"进行中 · "+active.length}>当前执行</h2>{active.map(runRow)}</section>}
      <Link className={active.length?'button secondary sop-create-secondary':'button primary'} to="/sop/templates/new">新建 SOP</Link>
      <section className="module-section"><h2 aria-label={'全部模板 · '+rows.templates.length}>模板</h2>{rows.templates.map(templateRow)}{!rows.templates.length&&<p className="quiet-empty">还没有 SOP</p>}</section>
      {!!rows.drafts.length&&<ListRow to="/sop/drafts" title="模板草稿" note={rows.drafts.length+' 份待继续'}/>}
      {!!history.length&&<section className="module-section"><div className="section-heading"><h2>最近执行</h2><Link className="button quiet" to="/sop/history">查看全部</Link></div>{history.slice(0,2).map(runRow)}</section>}
    </>}
    {view==='history'&&<><p className="list-context">{history.length} 次执行</p>{history.map(runRow)}{!history.length&&<p className="quiet-empty">还没有执行历史</p>}</>}
    {view==='pinned'&&<section><h2>固定 · {pinned.length}</h2>{pinned.map(templateRow)}</section>}
    {view==='recent'&&<section><h2>最近使用 · {recent.length}</h2>{recent.map(templateRow)}</section>}
    {view==='drafts'&&<section><h2>模板草稿 · {rows.drafts.length}</h2>{rows.drafts.map(d=><ListRow key={d.id} to={`/sop/templates/${d.targetId}/edit?draft=${d.id}`} title={d.payload.title||'未命名 SOP'} note="草稿 · 尚未发布"/>)}</section>}
    {view==='manage'&&<nav aria-label="SOP 管理">{[['/sop/pinned','固定模板'],['/sop/recent','最近使用'],['/sop/drafts','模板草稿'],['/sop/history','执行历史'],['/search','搜索'],['/trash','回收站']].map(([to,title])=><ListRow key={to} to={to} title={title}/>)}</nav>}
  </main>;
}
export function SopForm() {
  const { db } = useService(),
    { id } = useParams(),
    [params] = useSearchParams(),
    navigate = useNavigate(),
    s = new SopService(db);
  const [fields, setFields] = useState<SopFields>(blankSop),
    [ready, setReady] = useState(false),
    [pending, setPending] = useState(0),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [error, setError] = useState(false);
  const state = useRef({
      targetId: id ?? crypto.randomUUID(),
      baseRevision: 0,
      draftId: params.get("draft") ?? crypto.randomUUID(),
      draftRevision: 0,
    }),
    queue = useRef(Promise.resolve()),
    live = useRef(true),
    saveFailed = useRef(false);
  usePendingNavigation(pending > 0 || busy || saveFailed.current);
  useEffect(() => {
    live.current = true;
    // Our own saved-draft URL promotion must not reload a stale payload over live edits.
    if (id === state.current.targetId && params.get("draft") === state.current.draftId && state.current.draftRevision > 0) {
      return () => { live.current = false; };
    }
    let cancelled = false;
    void (async () => {
      const d = params.get("draft")
        ? await db.sopDrafts.get(params.get("draft")!)
        : undefined;
      if (d) {
        if (d.deletedAt || (id && d.targetId !== id))
          throw Error("草稿不可编辑");
        state.current = {
          targetId: d.targetId,
          baseRevision: d.baseRevision,
          draftId: d.id,
          draftRevision: d.revision,
        };
        if (!cancelled) {
          setFields(d.payload);
          setMessage("草稿已保存");
        }
      } else if (id) {
        const t = await db.sopTemplates.get(id);
        if (!t || t.deletedAt) throw Error("模板不存在或已在回收站");
        const v = await db.sopTemplateVersions.get(t.currentVersionId);
        if (!v) throw Error("模板版本缺失");
        state.current.baseRevision = t.revision;
        if (!cancelled)
          setFields({
            title: v.title,
            purpose: v.purpose,
            preparation: v.preparation,
            steps: v.steps,
          });
      }
      if (!cancelled) setReady(true);
    })().catch((e) => setMessage(e.message));
    return () => {
      cancelled = true;
      live.current = false;
    };
  }, [db, id]);
  function change(next: SopFields) {
    setFields(next);
    setPending((n) => n + 1);
    setMessage("正在保存草稿…");
    queue.current = queue.current.then(async () => {
      try {
        const x = state.current,
          d = await s.saveDraft(
            x.targetId,
            x.baseRevision,
            next,
            x.draftId,
            x.draftRevision,
          );
        state.current.draftId = d.id;
        state.current.draftRevision = d.revision;
        if (live.current) {
          saveFailed.current = false;
          setError(false);
          setMessage("草稿已保存");
        }
      } catch (e) {
        if (live.current) {
          saveFailed.current = true;
          setError(true);
          setMessage((e as Error).message + "；当前输入仍保留，请重试或导出。");
        }
      } finally {
        if (live.current) setPending((n) => n - 1);
      }
    });
  }
  useEffect(() => {
    if (
      ready &&
      !pending &&
      !busy &&
      state.current.draftRevision &&
      !error &&
      params.get("draft") !== state.current.draftId
    )
      navigate(
        `/sop/templates/${state.current.targetId}/edit?draft=${state.current.draftId}`,
        { replace: true },
      );
  }, [ready, pending, busy, error, navigate, params]);
  async function publish(copy = false) {
    setBusy(true);
    await queue.current;
    try {
      if (saveFailed.current) throw Error("草稿尚未保存，请先重试");
      if (!state.current.draftRevision) {
        const x = state.current,
          d = await s.saveDraft(
            x.targetId,
            x.baseRevision,
            fields,
            x.draftId,
            0,
          );
        x.draftRevision = d.revision;
      }
      const t = await s.publish(
        state.current.draftId,
        state.current.draftRevision,
        crypto.randomUUID(),
        copy,
      );
      navigate("/sop/templates/" + t.id);
    } catch (e) {
      setMessage((e as Error).message);
      setError(true);
    } finally {
      setBusy(false);
    }
  }
  const editStep = (
    index: number,
    patch: Partial<SopFields["steps"][number]>,
  ) =>
    change({
      ...fields,
      steps: fields.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    });
  return (
    <main className="page item-form sop-editor" id="main">
      <Link className="back-link" to="/sop">保留草稿并返回 SOP</Link>
      <h1>{id ? "编辑 SOP" : "新建 SOP"}</h1>
      <p role={error ? "alert" : "status"}>{message}</p>
      {ready && (
        <fieldset disabled={busy}>
          <label>
            流程名称
            <Input
              value={fields.title}
              onChange={(e) => change({ ...fields, title: e.target.value })}
            />
          </label>
          {fields.steps.map((step, index) => (
            <section className="history-row" key={step.id}>
              <h2>步骤 {index + 1}</h2>
              <label>
                步骤名称
                <Input
                  value={step.title}
                  onChange={(e) => editStep(index, { title: e.target.value })}
                />
              </label>
              <label>
                分组名称（留空为线性步骤）
                <Input
                  value={step.group}
                  onChange={(e) => editStep(index, { group: e.target.value })}
                />
              </label>
              <label>
                步骤说明
                <Textarea
                  value={step.body}
                  onChange={(e) => editStep(index, { body: e.target.value })}
                />
              </label>
              <label>
                <Checkbox
                  checked={step.required}
                  onChange={(e) =>
                    editStep(index, { required: e.target.checked })
                  }
                />
                必做
              </label>
              <Button
                disabled={index === 0}
                onClick={() => {
                  const steps = [...fields.steps];
                  [steps[index - 1], steps[index]] = [
                    steps[index],
                    steps[index - 1],
                  ];
                  change({ ...fields, steps });
                }}
              >
                上移步骤
              </Button>
              <Button
                onClick={() =>
                  change({
                    ...fields,
                    steps: fields.steps.filter((s) => s.id !== step.id),
                  })
                }
              >
                移除步骤
              </Button>
            </section>
          ))}
          <section className="form-section"><h2>用途与准备</h2>
          <label>
            用途
            <Textarea
              value={fields.purpose}
              onChange={(e) => change({ ...fields, purpose: e.target.value })}
            />
          </label>
          <label>
            准备物品
            <Textarea
              value={fields.preparation}
              onChange={(e) =>
                change({ ...fields, preparation: e.target.value })
              }
            />
          </label>
          </section>
          <Button
            disabled={fields.steps.length >= 200}
            onClick={() =>
              change({
                ...fields,
                steps: [
                  ...fields.steps,
                  {
                    id: crypto.randomUUID(),
                    group: "",
                    title: "",
                    body: "",
                    required: true,
                  },
                ],
              })
            }
          >
            添加步骤
          </Button>
          <div className="detail-actions">
            <Button
              className="button primary"
              disabled={busy || pending > 0}
              onClick={() => void publish()}
            >
              发布模板版本
            </Button>
            {error && state.current.baseRevision > 0 && (
              <Button onClick={() => void publish(true)}>
                保留原模板，另存新模板
              </Button>
            )}
            <Button disabled={!error} onClick={() => change(fields)}>
              重试保存草稿
            </Button>
            <Button
              onClick={() => {
                const url = URL.createObjectURL(
                  new Blob([JSON.stringify(fields, null, 2)], {
                    type: "application/json",
                  }),
                );
                const a = document.createElement("a");
                a.href = url;
                a.download = "Nook-SOP-当前输入.json";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              导出当前输入
            </Button>
            {state.current.draftRevision > 0 && (
              <Button
                disabled={pending > 0 || busy}
                onClick={() => {
                  if (confirm("将此模板草稿移入回收站？")) {
                    setBusy(true);
                    void s
                      .lifecycle(
                        "sop-draft",
                        state.current.draftId,
                        state.current.draftRevision,
                        "trash",
                        crypto.randomUUID(),
                      )
                      .then(() => navigate("/sop"))
                      .catch((e) => setMessage(e.message))
                      .finally(() => setBusy(false));
                  }
                }}
              >
                放弃这份草稿
              </Button>
            )}
          </div>
        </fieldset>
      )}
    </main>
  );
}
export function SopTemplateDetail() {
  const { db } = useService(),
    { id } = useParams(),
    navigate = useNavigate(),
    s = new SopService(db),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  usePendingNavigation(busy);
  const startOp = useRef(crypto.randomUUID());
  const data = useLiveQuery(async () => {
    const t = await db.sopTemplates.get(id!);
    if (!t) return null;
    return {
      t,
      v: (await db.sopTemplateVersions.get(t.currentVersionId))!,
      versions: await db.sopTemplateVersions
        .where("templateId")
        .equals(t.id)
        .toArray(),
      runs: await db.sopRuns.where("templateId").equals(t.id).toArray(),
      history: await db.revisions
        .where("[entityType+entityId]")
        .equals(["sop-template", t.id])
        .toArray(),
      drafts: await db.sopDrafts.where("targetId").equals(t.id).toArray(),
    };
  }, [db, id]);
  async function action(work: () => Promise<unknown>) {
    if (busy) return;
    setBusy(true);
    try {
      await work();
      setMessage("已保存");
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  if (data===undefined) return <LoadingPage />;
  if (!data) return <main className="page" id="main"><h1>未找到模板</h1><Link to="/sop">返回 SOP</Link></main>;
  const { t, v } = data;
  return (
    <main className="page sop-detail" id="main">
      <Link className="back-link" to="/sop">返回 SOP</Link>
      <h1>{v.title}</h1>
      <p>
        版本 {v.version}
        {t.archivedAt ? " · 归档" : ""}
        {t.deletedAt ? " · 回收站" : ""}
      </p>
      {v.purpose&&<><h2>用途</h2><p className="capture-body">{v.purpose}</p></>}
      {v.preparation&&<><h2>准备物品</h2><p className="capture-body">{v.preparation}</p></>}
      <Checklist version={v} />
      <p role="status">{message}</p>
      {!t.deletedAt && (
        <>
          <Link to={"/sop/templates/" + t.id + "/edit"}>编辑模板</Link>
          {data.drafts
            .filter((d) => !d.deletedAt)
            .map((d) => (
              <Link
                className="record-row"
                key={d.id}
                to={`/sop/templates/${t.id}/edit?draft=${d.id}`}
              >
                模板草稿 · {d.payload.title}
              </Link>
            ))}
          <Button
            disabled={busy}
            className="button primary"
            onClick={() =>
              void action(async () => {
                const r = await s.start(
                  t.id,
                  v.id,
                  t.revision,
                  startOp.current,
                );
                navigate("/sop/runs/" + r.id);
              })
            }
          >
            开始这一次
          </Button>
        </>
      )}
      <div className="detail-actions">
        {(t.deletedAt ? ["restore", "purge"] : ["archive", "pin", "trash"]).map(
          (a) => {
            const act =
              a === "archive" && t.archivedAt
                ? "unarchive"
                : a === "pin" && t.pinned
                  ? "unpin"
                  : a;
            return (
              <Button
                key={a}
                disabled={busy}
                onClick={() => {
                  if (
                    act === "trash" &&
                    !confirm(
                      `移入回收站？已有 ${data.runs.length} 次执行与 ${data.drafts.length} 份草稿仍保留原版本，不会被连带删除。`,
                    )
                  )
                    return;
                  if (
                    act === "purge" &&
                    !confirm(
                      "永久删除模板及版本与历史？仍被任何执行或草稿引用时将拒绝。",
                    )
                  )
                    return;
                  void action(async () => {
                    await s.lifecycle(
                      "sop-template",
                      t.id,
                      t.revision,
                      act as "archive",
                      crypto.randomUUID(),
                    );
                    if (act === "purge") navigate("/sop");
                  });
                }}
              >
                {
                  {
                    archive: "归档",
                    unarchive: "取消归档",
                    pin: "固定模板",
                    unpin: "取消固定",
                    trash: "移入回收站",
                    restore: "从回收站恢复",
                    purge: "永久删除",
                  }[act]
                }
              </Button>
            );
          },
        )}
      </div>
      <h2>模板版本 · {data.versions.length}</h2>
      {data.versions
        .sort((a, b) => b.version - a.version)
        .map((version) => (
          <Disclosure key={version.id}>
            <summary>
              版本 {version.version} · {new Date(version.createdAt).toLocaleString("zh-CN")}
            </summary>
            <h3>{version.title}</h3>
            <p>{version.purpose}</p>
            <p>{version.preparation}</p>
            <Checklist version={version} />
          </Disclosure>
        ))}
      <h2>每次执行 · {data.runs.length}</h2>
      {data.runs.map((r) => (
        <Link className="record-row" key={r.id} to={"/sop/runs/" + r.id}>
          {sopStateLabels[r.state]} · {new Date(r.createdAt).toLocaleString("zh-CN")}
          {r.deletedAt ? " · 回收站" : ""}
        </Link>
      ))}
      <Disclosure>
        <summary>模板修订历史 · {data.history.length}</summary>
        {data.history
          .filter((h) => "entityType" in h && h.entityType === "sop-template")
          .map((h) => (
            <section className="history-row" key={h.id}>
              <p>
                修订 {h.revision} · {h.reason} · {new Date(h.recordedAt).toLocaleString("zh-CN")}
              </p>
              {!t.deletedAt && (
                <Button
                  disabled={busy}
                  onClick={() => {
                    if (confirm("用此历史模板新增不可变版本？既有执行不改变。"))
                      void action(() =>
                        s.templateHistoryRestore(
                          t.id,
                          t.revision,
                          h.id,
                          crypto.randomUUID(),
                        ),
                      );
                  }}
                >
                  以此历史新增版本
                </Button>
              )}
            </section>
          ))}
      </Disclosure>
    </main>
  );
}
export function SopRunDetail() {
  const { db } = useService(),
    { id } = useParams(),
    navigate = useNavigate(),
    s = new SopService(db);
  const [pending, setPending] = useState(0),
    [message, setMessage] = useState(""),
    [ending, setEnding] = useState(false),
    [outcome, setOutcome] = useState("");
  const queue = useRef(Promise.resolve()),
    current = useRef<SopRun | undefined>(undefined),
    finishOp = useRef(crypto.randomUUID()),
    unsavedResults = useRef(new Map<string, Partial<SopResult>>());
  usePendingNavigation(pending > 0 || unsavedResults.current.size > 0);
  const data = useLiveQuery(async () => {
    const r = await db.sopRuns.get(id!);
    if (!r) return null;
    return {
      r,
      v: (await db.sopTemplateVersions.get(r.templateVersionId))!,
      template: await db.sopTemplates.get(r.templateId),
      history: await db.revisions
        .where("[entityType+entityId]")
        .equals(["sop-run", r.id])
        .toArray(),
    };
  }, [db, id]);
  useEffect(() => {
    if (data && !pending) current.current = data.r;
  }, [data, pending]);
  function enqueue(work: (r: SopRun) => Promise<SopRun | void>) {
    setPending((n) => n + 1);
    setMessage("正在保存…");
    queue.current = queue.current.then(async () => {
      try {
        const r = current.current ?? data?.r;
        if (!r) throw Error("执行尚未读取");
        const result = await work(r);
        if (result) current.current = result;
        setMessage("进度已保存");
      } catch (e) {
        setMessage((e as Error).message);
      } finally {
        setPending((n) => n - 1);
      }
    });
  }
  function result(stepId: string, patch: Partial<SopResult>) {
    const retained = { ...unsavedResults.current.get(stepId), ...patch };
    unsavedResults.current.set(stepId, retained);
    enqueue(async (r) => {
      const next = await s.updateRun(
        r.id,
        r.revision,
        {
          action: "step",
          advance: patch.status === "completed",
          result: {
            ...r.results.find((x) => x.stepId === stepId)!,
            ...retained,
          },
        },
        crypto.randomUUID(),
      );
      if (
        JSON.stringify(unsavedResults.current.get(stepId)) ===
        JSON.stringify(retained)
      )
        unsavedResults.current.delete(stepId);
      return next;
    });
  }
  if (data===undefined) return <LoadingPage />;
  if (!data) return <main className="page" id="main"><h1>未找到执行</h1><Link to="/sop">返回 SOP</Link></main>;
  const { r, v, template } = data,
    active = r.state === "active" && !r.deletedAt,
    requiredRemaining = v.steps.filter(
      (step, i) => step.required && r.results[i].status !== "completed",
    ),
    ended = !["active", "paused"].includes(r.state);
  return (
    <main className="page sop-execution" id="main">
      <Link className="back-link" to="/sop">返回 SOP（保留进度）</Link>
      <h1>{v.title}</h1>
      <p>
        本次版本 {v.version} · {sopStateLabels[r.state]}
        {r.archivedAt ? " · 归档" : ""}
        {r.deletedAt ? " · 回收站" : ""}
      </p>
      <p>
        已完成 {r.results.filter((x) => x.status === "completed").length}/
        {v.steps.length} · 当前位置 {r.cursor + 1}/{v.steps.length}
      </p>
      {template?.currentVersionId !== v.id && (
        <p className="notice">
          模板已有新版本，本次仍使用 v{v.version}。
          <Link to={"/sop/templates/" + r.templateId}>查看模板变化</Link>
        </p>
      )}
      <p role="status">{message}</p>
      {unsavedResults.current.size > 0 && pending === 0 && (
        <div className="notice">
          <p>有步骤输入尚未保存，请重试或导出当前输入；暂停和结束暂不可用。</p>
          <Button
            onClick={() => {
              for (const stepId of unsavedResults.current.keys())
                result(stepId, {});
            }}
          >
            以当前版本重试未保存输入
          </Button>
          <Button
            onClick={() => {
              const url = URL.createObjectURL(
                new Blob(
                  [
                    JSON.stringify(
                      {
                        runId: r.id,
                        templateVersionId: r.templateVersionId,
                        unsaved: [...unsavedResults.current],
                      },
                      null,
                      2,
                    ),
                  ],
                  { type: "application/json" },
                ),
              );
              const a = document.createElement("a");
              a.href = url;
              a.download = "Nook-SOP-未保存步骤.json";
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            导出本次未保存输入
          </Button>
        </div>
      )}
      {r.state === "paused" && !r.deletedAt && (
        <Button
          disabled={pending > 0}
          onClick={() =>
            enqueue((x) =>
              s.updateRun(
                x.id,
                x.revision,
                { action: "resume" },
                crypto.randomUUID(),
              ),
            )
          }
        >
          继续这一次
        </Button>
      )}
      <h2>完整执行清单</h2>
      <ol className="sop-step-list">
        {v.steps.map((step, i) => {
          const value = r.results[i];
          return (
            <li
              className="history-row"
              key={step.id}
              data-current={r.cursor === i ? "true" : "false"}
            >
              <h3>
                {step.group && <span>{step.group} / </span>}
                {step.title} · {step.required ? "必做" : "选做"}
                {r.cursor === i ? " · 当前步骤" : ""}
              </h3>
              <p className="capture-body">{step.body}</p>
              <p>状态：{sopResultLabels[value.status]}</p>
              {active ? (
                <>
                  <label>
                    步骤备注
                    <Textarea
                      defaultValue={value.note}
                      onChange={(e) =>
                        result(step.id, { note: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    跳过 / 不适用原因
                    <Input
                      defaultValue={value.reason}
                      onChange={(e) =>
                        result(step.id, { reason: e.target.value })
                      }
                    />
                  </label>
                  <Button
                    className="button primary"
                    disabled={pending > 0}
                    onClick={() => result(step.id, { status: "completed" })}
                  >
                    完成此步骤
                  </Button>
                  <Button
                    disabled={pending > 0}
                    onClick={() => result(step.id, { status: "pending" })}
                  >
                    恢复未完成
                  </Button>
                  <Button
                    disabled={pending > 0}
                    onClick={() => result(step.id, { status: "skipped" })}
                  >
                    跳过此步骤
                  </Button>
                  <Button
                    disabled={pending > 0}
                    onClick={() =>
                      result(step.id, { status: "not-applicable" })
                    }
                  >
                    不适用
                  </Button>
                  <Button
                    disabled={pending > 0}
                    onClick={() =>
                      enqueue((x) =>
                        s.updateRun(
                          x.id,
                          x.revision,
                          { action: "cursor", cursor: i },
                          crypto.randomUUID(),
                        ),
                      )
                    }
                  >
                    查看此步骤
                  </Button>
                </>
              ) : (
                <>
                  <p>备注：{value.note || "无"}</p>
                  <p>原因：{value.reason || "无"}</p>
                </>
              )}
            </li>
          );
        })}
      </ol>
      {active && (
        <div className="detail-actions">
          <Button
            disabled={pending > 0 || unsavedResults.current.size > 0}
            onClick={() =>
              enqueue(async (x) => {
                const next = await s.updateRun(
                  x.id,
                  x.revision,
                  { action: "pause" },
                  crypto.randomUUID(),
                );
                navigate("/sop");
                return next;
              })
            }
          >
            暂停并退出
          </Button>
          <Button
            disabled={pending > 0 || unsavedResults.current.size > 0}
            onClick={() => {
              if (!unsavedResults.current.size) setEnding(true);
            }}
          >
            {requiredRemaining.length ? "查看未处理步骤" : "结束这一次"}
          </Button>
        </div>
      )}
      {ending && active && (
        <section className="notice">
          <h2>结束确认</h2>
          <p>尚未完成必做步骤：{requiredRemaining.length}</p>
          <ul>
            {requiredRemaining.map((s) => (
              <li key={s.id}>{s.title}</li>
            ))}
          </ul>
          <label>
            结束原因
            <Textarea
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
            />
          </label>
          <Button onClick={() => setEnding(false)}>返回处理</Button>
          <Button
            disabled={pending > 0 || requiredRemaining.length > 0}
            onClick={() =>
              enqueue((x) =>
                s.updateRun(
                  x.id,
                  x.revision,
                  { action: "completed", reason: outcome },
                  finishOp.current,
                ),
              )
            }
          >
            全部完成并结束
          </Button>
          <Button
            disabled={pending > 0 || !outcome.trim()}
            onClick={() =>
              enqueue((x) =>
                s.updateRun(
                  x.id,
                  x.revision,
                  { action: "partial", reason: outcome },
                  finishOp.current,
                ),
              )
            }
          >
            部分完成并结束
          </Button>
          <Button
            disabled={pending > 0 || !outcome.trim()}
            onClick={() =>
              enqueue((x) =>
                s.updateRun(
                  x.id,
                  x.revision,
                  { action: "cancelled", reason: outcome },
                  finishOp.current,
                ),
              )
            }
          >
            取消这一次
          </Button>
        </section>
      )}
      {ended && (
        <section>
          <h2>本次结果：{sopStateLabels[r.state]}</h2>
          <p>结束：{r.endedAt}</p>
          <p className="capture-body">原因：{r.outcomeReason || "无"}</p>
          {template && !template.deletedAt && (
            <Link to={"/sop/templates/" + r.templateId}>
              查看模板并开始新的一次
            </Link>
          )}
        </section>
      )}
      <div className="detail-actions">
        {(r.deletedAt ? ["restore", "purge"] : ["archive", "trash"]).map(
          (a) => {
            const action = a === "archive" && r.archivedAt ? "unarchive" : a;
            return (
              <Button
                key={a}
                disabled={pending > 0}
                onClick={() => {
                  if (
                    action === "trash" &&
                    !confirm("将本次执行移入回收站？步骤、原版本和历史保留。")
                  )
                    return;
                  if (
                    action === "purge" &&
                    !confirm("永久删除本次执行及修订历史？模板与其他执行保留。")
                  )
                    return;
                  enqueue(async (x) => {
                    await s.lifecycle(
                      "sop-run",
                      x.id,
                      x.revision,
                      action as "archive",
                      crypto.randomUUID(),
                    );
                    if (action === "purge") {
                      navigate("/sop");
                      return;
                    }
                    return db.sopRuns.get(x.id);
                  });
                }}
              >
                {
                  {
                    archive: "归档本次执行",
                    unarchive: "取消归档",
                    trash: "移入回收站",
                    restore: "从回收站恢复",
                    purge: "永久删除",
                  }[action]
                }
              </Button>
            );
          },
        )}
      </div>
      <Disclosure>
        <summary>本次执行修订历史 · {data.history.length}</summary>
        {data.history
          .filter(
            (h) =>
              "entityType" in h &&
              h.entityType === "sop-run" &&
              "results" in h.snapshot,
          )
          .map((h) => (
            <section className="history-row" key={h.id}>
              <p>
                修订 {h.revision} · {h.reason} · {new Date(h.recordedAt).toLocaleString("zh-CN")}
              </p>
              {"results" in h.snapshot && (
                <>
                  <p>
                    {sopStateLabels[h.snapshot.state]} · 当前位置{" "}
                    {h.snapshot.cursor + 1} · 原因：{h.snapshot.outcomeReason}
                  </p>
                  {h.snapshot.results.map((x, i) => (
                    <p key={x.stepId}>
                      {v.steps[i].title} · {sopResultLabels[x.status]} · 备注：
                      {x.note} · 原因：{x.reason}
                    </p>
                  ))}
                </>
              )}
            </section>
          ))}
      </Disclosure>
    </main>
  );
}
